import { redirect } from '@tanstack/react-router';
import type { Principal } from '../api/types';
import type { AuthStore } from './auth-store';

/** Waits for the session; sends a signed-out visitor to sign-in with a way back. */
export async function requireSignIn(auth: AuthStore, href: string): Promise<Principal> {
  const principal = await auth.ensure();
  if (!principal) throw redirect({ to: '/sign-in', search: { next: href } });
  return principal;
}

/** Staff only: the staff claim comes from the API's `/me`, never from the URL. */
export async function requireStaff(auth: AuthStore, href: string): Promise<Principal> {
  const principal = await requireSignIn(auth, href);
  if (!principal.staff) throw redirect({ to: '/' });
  return principal;
}

const LAST_COMPANY = 'paige.company';

/** The company the person opened last, so the next sign-in lands there; nothing else is remembered. */
export function rememberCompany(tenantId: string): void {
  try {
    localStorage.setItem(LAST_COMPANY, tenantId);
  } catch {
    // Storage may be unavailable; the first company is the fallback.
  }
}

export function lastCompany(): string | null {
  try {
    return localStorage.getItem(LAST_COMPANY);
  } catch {
    return null;
  }
}

/**
 * Where a signed-in principal lands: the company they opened last (or their first), the admin area
 * for staff without a company, or the sign-up's company step. There is no page between sign-in and
 * the jobs; other companies are one click away under the person's mark (Dustin, 2026-09-19).
 */
export function homeFor(
  p: Principal,
):
  | { to: '/t/$tenant'; params: { tenant: string } }
  | { to: '/admin' }
  | { to: '/sign-up'; search: { step: 'company' } } {
  const last = lastCompany();
  const tenant = p.tenants.find((t) => t.id === last) ?? p.tenants[0];
  if (tenant) return { to: '/t/$tenant', params: { tenant: tenant.id } };
  if (p.staff) return { to: '/admin' };
  return { to: '/sign-up', search: { step: 'company' } };
}

/**
 * The same address under the company's slug, when `named` is the id of a company the person is in:
 * `/t/{id}/settings/billing?x=1#y` becomes `/t/{slug}/settings/billing?x=1#y`. Null when `named`
 * is not one of their companies' ids (a slug, a stranger's company, anything else).
 */
export function slugAddress(
  p: Principal,
  named: string,
  location: { pathname: string; search: unknown; hash: string },
): { to: string; search: never; hash: string; replace: true } | null {
  const company = p.tenants.find(
    (t) => t.tenantId !== undefined && t.tenantId.toLowerCase() === named.toLowerCase(),
  );
  const prefix = `/t/${named}`;
  if (!company || !location.pathname.startsWith(prefix)) return null;
  return {
    to: `/t/${company.id}${location.pathname.slice(prefix.length)}`,
    search: location.search as never,
    hash: location.hash,
    replace: true,
  };
}
